<script>
    
    import { fade } from 'svelte/transition';
    import { onMount } from 'svelte';

    import StartPanelv2 from "./StartPanelv2.svelte";
    import Sessionv2 from "./Sessionv2.svelte";
    import Alert from './Alert.svelte';
    import ABRAlert from './ABRAlert.svelte';
    import DuplicateEngAlert from './DuplicateEngAlert.svelte';
    import TalentConstructionAlert from './TalentConstructionAlert.svelte';

    import { Conference } from "../../Conference/Conference.js";
    import { Devices } from "../../Devices.js";
    import { OutputDevices } from "../../OutputDevices.js";
    import { Dispatcher } from "../../Dispatcher.js"
    import { OutputConfig } from '../../OutputConfig';
    import { DTSInterface } from "../../DTSInterface.js";
    import { Mixer } from "../../Mixer.js";
    import { MediaLifecycle } from "../../MediaLifecycle.js";
    import { Logger } from "../../Logger.js";

    //import { CoreMetrics } from "../../Core/CoreMetrics.js"

    import { conf_conn_ok, 
    		needs_user_resume, 
      		has_joined, 
        	connection_status,
        	start_panel_open,
        	duplicateEngAlert } from '../../stores.js';

    export let self_type;
    export let room_name;
    export let conf_disabled;


    // -- How long the start panel cross-fades with the session, in both directions: the
    // -- wrapper's transition:fade below, and the delay that keeps the session painted under
    // -- it on the way back in (see #session-wrapper.covered). A stylesheet can't read a JS
    // -- constant, so it also reaches the CSS as the --panel-fade custom property on #App.
    // -- One number, so the fade and the delay can't drift apart.
    const PANEL_FADE_MS = 200;

    // -- the talent role is still under construction: don't spin up the decoder thread,
    // -- audio contexts, devices, or the conference. We render only the blocking overlay.
    const is_talent = self_type === "talent";

    let show_start_panel = true;
    let devices_hidden = true;

    $: start_panel_open.set(show_start_panel);

    // -- the MOQ relay or the pl-ws signaling server has dropped (Jitsi self-heals on its own).
    // -- moq only counts when it's actually in use (it may have fallen back to ws at connect time).
    // -- gated on has_joined so it only surfaces in-session, not during pre-join connect.
    $: connection_lost = $has_joined
        && (!$connection_status.ws
            || ($connection_status.moq_in_use && !$connection_status.moq));

    // -- a reconnect loop is dialing (worker-side backoff); the banner says so instead of
    // -- "lost" -- most drops self-heal within seconds and need no user action
    $: reconnecting = $has_joined && $connection_status.reconnecting;

    // -- the reconnect budget is exhausted (or the server re-minted our id): the session is
    // -- unrecoverable without a reload, so the blocking overlay takes over from the banner
    $: gave_up = $has_joined && $connection_status.gave_up;

    // -- the connection banner can be dismissed with its ×. Re-arm on each fresh drop or
    // -- reconnect start (a false→true edge) so dismissing only hides the current outage.
    let banner_dismissed = false;
    let _was_connection_lost = false;
    $: track_connection_lost(connection_lost || reconnecting);
    function track_connection_lost(lost){
        if (lost && !_was_connection_lost) banner_dismissed = false;
        _was_connection_lost = lost;
    }
    
    // -- refused as a second eng (DuplicateEngAlert): this tab can never join, so release the
    // -- camera, mic, conference connection and decoder it opened at mount
    $: if ($duplicateEngAlert) release_refused_tab();
    function release_refused_tab(){
        Devices.release();
        Conference.cleanUp();
        Dispatcher.cleanUpDecoderThread();
    }

    navigator.mediaDevices.addEventListener('devicechange', async e => {
        await Devices.reconcileDevicesAfterChange();
        Mixer.reassessOutputLatency("devicechange");
    })

    /*
     on session enter -- turn devicesHidden to false, since when we come back to Start Panel, we want to skip the initial name step
    */
    function show_session(){
        
        OutputConfig.has_joined_session = true;
        
        // -- conference audio isn't rendered and relay streams are not sent until we join
        if (!$has_joined){
            Logger.log("Session", "info", "Session Entered", {conf_disabled: conf_disabled});
            has_joined.set(true);
            Mixer.unlockStream();
            Dispatcher.setCanTransmit(true);
        }

        show_start_panel = false;
    }

    function open_start_panel(){
        Logger.log("Session", "info", "Returned To Start Panel", {});
        devices_hidden = false;
        show_start_panel = true;
    }

    function on_resume_click(){

    	if (gave_up){
            Logger.log("Page Load", "info", "App Reload", {reason: "reconnect-tap"});
            Logger.flush(true);
            window.location.reload();
        }
        else
            MediaLifecycle.userResume();
    }
    
    const CONNECT_ATTEMPTS = 3;
    async function _connectConference(){

        for (let attempt = 1; attempt <= CONNECT_ATTEMPTS; attempt++){

            // -- a refused eng tab (release_refused_tab) can't join: don't dial, and drop a
            // -- connection that landed after the release ran
            if ($duplicateEngAlert) return;
            const conf_result = await Conference.connect(room_name);
            console.log(`[PL-Conference] Connect attempt ${attempt} finished with status: `, conf_result.status);
            if ($duplicateEngAlert){ Conference.cleanUp(); return; }

            if (conf_result.status === "ok"){ 
            	conf_conn_ok.set(true); 
             	return; 
            }

            if (attempt < CONNECT_ATTEMPTS)
                await new Promise(r => setTimeout(r, attempt * 4000));
        }

        console.error("Conference Connect Error -- SHOW UI HERE");
    }

    /// -- This is the last onMount after all child component onMounts
    onMount(async () => {

    	// -- under construction
        if (is_talent) return;

        try {

            const awp_port = await Dispatcher.initAudioContext();
            Dispatcher.initDecoderThread(room_name, self_type, awp_port);

            // -- this will presumably go here
            // if (self_type === "talent")
            //     await DTSInterface.initAudioContext().catch(e => {
            //         console.error(e);
            //         Logger.log("DTS", "error", "DTS Failed To Load", {error: e.message});
            //     });

            OutputDevices.init(self_type);
            Devices.init(self_type);

            // -- we only create a conference connection but do not yet join (I think under the hood this opens the ljm websocket)
            if (!conf_disabled) _connectConference();
            else conf_conn_ok.set(true);
        }

        catch (error){
            Logger.log("Init Modules", "error", "Fatal Loading Error", {error: error instanceof Error ? error.message : String(error)});
        }

    })

</script>

<div id="App" style="--panel-fade: {PANEL_FADE_MS}ms">

    {#if !is_talent}
        <div id="session-wrapper" class:covered={show_start_panel}>
            <Sessionv2 on:open_start_panel={open_start_panel}/>
        </div>

        {#if show_start_panel}
            <div id="start-panel-wrapper" transition:fade={{ duration: PANEL_FADE_MS }}>
                <StartPanelv2 on:show_session={show_session} self_type={self_type} conf_disabled={conf_disabled} devicesHidden={devices_hidden}/>
            </div>
        {/if}

        <Alert/>
        <ABRAlert/>
        <DuplicateEngAlert/>
    {/if}

    <TalentConstructionAlert active={is_talent}/>

    {#if !gave_up && (reconnecting || connection_lost) && !banner_dismissed}
        <div id="connection-banner" class:reconnecting transition:fade>
            <span class="dot"></span>
            {reconnecting ? 'Reconnecting…' : 'Connection lost'}
            <button class="banner-close" on:click={() => banner_dismissed = true} aria-label="Dismiss">×</button>
        </div>
    {/if}

    {#if $needs_user_resume || gave_up}
        <div id="resume-overlay" on:click={on_resume_click} transition:fade>
            <button class="resume-button">{gave_up ? 'Connection lost — tap to reload' : 'Tap to resume'}</button>
        </div>
    {/if}

</div>


<style>
    #App {
        position: relative;
        min-height: 100vh;
    }

    #session-wrapper {
        position: relative;
        z-index: 0;
    }

    /* -- z-order alone isn't enough to keep the start panel on top: iOS WebKit can composite
       -- the worker-rendered playback canvas above the panel until something forces a layer-tree
       -- rebuild (e.g. the camera preview starting after the permission prompt). So while the
       -- panel is up the session isn't painted at all. visibility (not display) so the session
       -- keeps its layout: canvas sizing/ResizeObserver and recomputeLayout stay live. The
       -- delay on hide is the panel's own fade duration (PANEL_FADE_MS, via --panel-fade),
       -- so when returning via settings the session stays visible under the fade instead of
       -- blinking out. Uncovering is instant -- that delay is what the session fades up from.
       */
    #session-wrapper.covered {
        visibility: hidden;
        transition: visibility 0s linear var(--panel-fade);
    }

    #start-panel-wrapper {
        position: absolute;
        inset: 0;
        z-index: 10;
        overflow-y: auto;
        background-color: rgb(10, 10, 10);
    }

    #connection-banner {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 200;

        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.6em;

        padding: 0.55em 1em;
        font-family: inherit;                          /* Quicksand, like the rest of the app */
        font-size: 0.95rem;
        letter-spacing: 0.02em;
        color: #ffe2e2;
        background: rgba(122, 31, 31, 0.92);           /* muted red, sits over everything */
        backdrop-filter: blur(4px);
    }

    #connection-banner .dot {
        width: 0.55em;
        height: 0.55em;
        border-radius: 50%;
        background: #ff5c5c;
        animation: connection-pulse 1.4s ease-in-out infinite;
    }

    /* -- while a reconnect loop is dialing: amber, not alarming -- most drops self-heal */
    #connection-banner.reconnecting {
        color: #ffedd0;
        background: rgba(122, 88, 22, 0.92);
    }

    #connection-banner.reconnecting .dot {
        background: #ffb84d;
    }

    @keyframes connection-pulse {
        0%, 100% { opacity: 1; }
        50%      { opacity: 0.3; }
    }

    #connection-banner .banner-close {
        position: absolute;
        right: 0.6em;
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
        background: none;
        border: none;
        color: inherit;
        font-size: 1.25rem;
        line-height: 1;
        padding: 0 0.3em;
        cursor: pointer;
        opacity: 0.75;
    }

    #connection-banner .banner-close:hover { opacity: 1; }

    #resume-overlay {
        position: fixed;
        inset: 0;
        z-index: 100;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(10, 10, 10, 0.7);
        cursor: pointer;
    }

    .resume-button {
        font-family: inherit;                          /* Quicksand, like the rest of the app */
        font-size: 1.7rem;                             /* matches the app's buttons (Join / OK) */
        font-weight: 400;
        color: #ffd5d5;

        padding: 0.7em 1.6em;
        border: 0.05em solid rgba(255, 255, 255, 0.372);
        border-radius: 8px;
        background: #00a48a91;                         /* app's primary action (the Join button) */

        cursor: pointer;
        transition: background 0.18s ease;
    }

    .resume-button:hover {
        background: #00887191;
    }

    /* overflow: hidden; Prevent horizontal and unnecessary vertical scrollbars */
    /* body, html {

        margin: 0;
        padding: 0;
        overflow-y: auto;
        height: 100%;
        box-sizing: border-box;
        background-color: rgb(10, 10, 10);
    } */


</style>